import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Users, 
  Copy, 
  Link2,
  Trash2, 
  Edit, 
  Shield, 
  Clock,
  Mail,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCESS_LEVELS = {
  full_access: { label: 'Full Access', description: 'Can view, add, edit, and delete all data', color: 'bg-emerald-100 text-emerald-700' },
  view_entry: { label: 'View + Entry', description: 'Can view reports and add new data', color: 'bg-blue-100 text-blue-700' },
  data_entry: { label: 'Data Entry Only', description: 'Can only add new data, no viewing reports', color: 'bg-amber-100 text-amber-700' },
};

const TeamPage = () => {
  const { user, isMainUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showEditInviteDialog, setShowEditInviteDialog] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [editInviteAccessLevel, setEditInviteAccessLevel] = useState('');
  const [inviteAccessLevel, setInviteAccessLevel] = useState('view_entry');
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [editAccessLevel, setEditAccessLevel] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [membersRes, invitesRes] = await Promise.all([
        axios.get(`${API_URL}/api/team/members`),
        axios.get(`${API_URL}/api/team/invites`)
      ]);
      setMembers(membersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isMainUser()) {
      fetchData();
    }
  }, [isMainUser, fetchData]);

  const createInvite = async () => {
    // Validate: name & email are required now
    if (!inviteName.trim() || !inviteEmail.trim()) {
      toast.error("Please enter the member's name and email");
      return;
    }
    // Simple email format check
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim());
    if (!emailOk) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      setCreatingInvite(true);
      const response = await axios.post(`${API_URL}/api/team/invite`, {
        access_level: inviteAccessLevel,
        invitee_name: inviteName.trim(),
        invitee_email: inviteEmail.trim().toLowerCase(),
      });
      setInvites([response.data, ...invites]);
      if (response.data.email_sent) {
        toast.success(`Invite emailed to ${response.data.invitee_email}`);
      } else if (response.data.signup_link) {
        toast.warning('Email could not be delivered — signup link copied to clipboard. Share it manually.', { duration: 8000 });
        try { await navigator.clipboard.writeText(response.data.signup_link); } catch (_e) { /* clipboard blocked */ }
      } else {
        toast.warning(`Invite created — share code ${response.data.code} manually with ${response.data.invitee_email}`);
      }
      setShowInviteDialog(false);
      setInviteName('');
      setInviteEmail('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create invite';
      toast.error(msg);
    } finally {
      setCreatingInvite(false);
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied to clipboard');
  };

  const copyInviteLink = (invite) => {
    const link = invite.signup_link || `${window.location.origin}/register?code=${invite.code}`;
    navigator.clipboard.writeText(link);
    toast.success('Signup link copied — paste it into email/WhatsApp for the invitee');
  };

  const resendInvite = async (inviteId) => {
    try {
      const r = await axios.post(`${API_URL}/api/team/invites/${inviteId}/resend`);
      if (r.data.email_sent) {
        toast.success('Invite email re-sent successfully');
      } else {
        toast.warning(r.data.email_error || 'Email delivery still failing — use Copy Link instead', { duration: 8000 });
      }
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const deleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API_URL}/api/team/invites/${inviteId}`);
      setInvites(invites.filter(i => i.id !== inviteId));
      toast.success('Invite code deleted');
    } catch (error) {
      toast.error('Failed to delete invite');
    }
  };

  const updateMember = async () => {
    if (!selectedMember) return;
    try {
      await axios.put(`${API_URL}/api/team/members/${selectedMember.id}`, { access_level: editAccessLevel });
      setMembers(members.map(m => m.id === selectedMember.id ? { ...m, access_level: editAccessLevel } : m));
      toast.success('Member updated');
      setShowEditDialog(false);
      setSelectedMember(null);
    } catch (error) {
      toast.error('Failed to update member');
    }
  };

  const removeMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;
    try {
      await axios.delete(`${API_URL}/api/team/members/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const toggleMemberDigest = async (memberId, enabled) => {
    // Optimistic UI: flip state immediately, revert on error
    setMembers(members.map(m => m.id === memberId ? { ...m, daily_digest_enabled: enabled } : m));
    try {
      await axios.put(`${API_URL}/api/team/members/${memberId}/digest`, { enabled });
      toast.success(`Daily digest ${enabled ? 'enabled' : 'disabled'} for this member`);
    } catch (error) {
      setMembers(members.map(m => m.id === memberId ? { ...m, daily_digest_enabled: !enabled } : m));
      toast.error(error.response?.data?.detail || 'Failed to update digest preference');
    }
  };

  const openEditDialog = (member) => {
    setSelectedMember(member);
    setEditAccessLevel(member.access_level);
    setShowEditDialog(true);
  };

  const openEditInviteDialog = (invite) => {
    setSelectedInvite(invite);
    setEditInviteAccessLevel(invite.access_level);
    setShowEditInviteDialog(true);
  };

  const updateInvite = async () => {
    if (!selectedInvite) return;
    try {
      const res = await axios.patch(
        `${API_URL}/api/team/invites/${selectedInvite.id}`,
        { access_level: editInviteAccessLevel },
      );
      setInvites(invites.map(i => (i.id === selectedInvite.id ? res.data : i)));
      toast.success('Invite updated');
      setShowEditInviteDialog(false);
      setSelectedInvite(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invite');
    }
  };

  // If not main user, show access denied
  if (!isMainUser()) {
    return (
      <div className="p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-gray-200">
            <CardContent className="pt-12 pb-12 text-center">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Restricted</h2>
              <p className="text-gray-500">Only the account owner can manage team members.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Team Management</h1>
            <p className="text-gray-500 mt-1">Invite and manage team members for data entry</p>
          </div>
          <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gray-800 hover:bg-gray-700" data-testid="create-invite-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  We'll email a signup link to your teammate so they can join in one click.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="invitee-name">Name</Label>
                  <input
                    id="invitee-name"
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="e.g. Sipho Mokoena"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="invite-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invitee-email">Email</Label>
                  <input
                    id="invitee-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@company.co.za"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role / Access Level</Label>
                  <Select value={inviteAccessLevel} onValueChange={setInviteAccessLevel}>
                    <SelectTrigger data-testid="invite-access-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACCESS_LEVELS).map(([key, { label, description }]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex flex-col">
                            <span>{label}</span>
                            <span className="text-xs text-gray-500">{description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>How it works:</strong> We email a signup link + invite code that expires in 7 days.
                    The teammate clicks the link, sets a password, and lands right in your workspace.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
                <Button onClick={createInvite} disabled={creatingInvite} data-testid="confirm-create-invite-btn">
                  {creatingInvite ? 'Sending...' : 'Send Invite'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <Users className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{members.length}</p>
                  <p className="text-sm text-gray-500">Team Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{invites.filter(i => i.used).length}</p>
                  <p className="text-sm text-gray-500">Used Invites</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{invites.filter(i => !i.used).length}</p>
                  <p className="text-sm text-gray-500">Pending Invites</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team Members */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Team Members</CardTitle>
            <CardDescription>People who have joined your team</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : members.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No team members yet</p>
                <p className="text-sm text-gray-400">Create an invite to add team members</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Access Level</TableHead>
                    <TableHead className="text-center" title="Send this member the daily Fleet Health digest at 06:00 SAST">Daily Digest</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id} data-testid={`member-row-${member.id}`}>
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-gray-500">
                          <Mail className="w-4 h-4" />
                          {member.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={ACCESS_LEVELS[member.access_level]?.color || 'bg-gray-100'}>
                          {ACCESS_LEVELS[member.access_level]?.label || member.access_level}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <input
                          type="checkbox"
                          checked={!!member.daily_digest_enabled}
                          onChange={(e) => toggleMemberDigest(member.id, e.target.checked)}
                          className="h-4 w-4 accent-obsidian cursor-pointer"
                          data-testid={`member-digest-toggle-${member.id}`}
                          title="Send daily Fleet Health digest to this member"
                        />
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {member.last_login ? new Date(member.last_login).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openEditDialog(member)}
                            data-testid={`edit-member-${member.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeMember(member.id)}
                            data-testid={`remove-member-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Invite Codes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pending Invites</CardTitle>
            <CardDescription>Team invites you've sent — the signup link goes straight to their email inbox.</CardDescription>
          </CardHeader>
          <CardContent>
            {invites.some(i => !i.used && i.invitee_email && i.email_sent === false) && (
              <div
                className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                data-testid="invite-delivery-banner"
              >
                <Mail className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                <div>
                  <p className="font-semibold">Some invites failed to deliver by email.</p>
                  <p className="text-xs mt-1">
                    Resend requires a verified sender domain to email non-account addresses.
                    Add SPF/DKIM records for <code className="font-mono">fleetcost.app</code> at{' '}
                    <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a>,
                    then click the mail icon on any pending invite to retry. In the meantime, use the <strong>Copy Link</strong> button to share the signup link via WhatsApp or another channel.
                  </p>
                </div>
              </div>
            )}
            {invites.length === 0 ? (
              <div className="text-center py-8">
                <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No invites yet</p>
                <p className="text-sm text-gray-400">Click &quot;Invite Member&quot; to send one</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.id} data-testid={`invite-row-${invite.id}`}>
                      <TableCell className="font-medium">{invite.invitee_name || '—'}</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {invite.invitee_email || '—'}
                        {invite.invitee_email && invite.email_sent === false && (
                          <span
                            className="ml-2 text-[10px] uppercase tracking-wider text-amber-600 font-mono cursor-help"
                            title={invite.email_error || 'Email delivery failed. Use the Copy Link button and share manually.'}
                            data-testid={`invite-not-delivered-${invite.id}`}
                          >
                            not delivered
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={ACCESS_LEVELS[invite.access_level]?.color || 'bg-gray-100'}>
                          {ACCESS_LEVELS[invite.access_level]?.label || invite.access_level}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                          {invite.code}
                        </code>
                      </TableCell>
                      <TableCell>
                        {invite.used ? (
                          <Badge className="bg-gray-100 text-gray-600">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Used
                          </Badge>
                        ) : new Date(invite.expires_at) < new Date() ? (
                          <Badge className="bg-red-100 text-red-600">
                            <XCircle className="w-3 h-3 mr-1" />
                            Expired
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-600">
                            <Clock className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {new Date(invite.expires_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!invite.used && (
                            <>
                              {invite.invitee_email && invite.email_sent === false && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => resendInvite(invite.id)}
                                  title="Resend invite email (once your Resend domain is verified)"
                                  data-testid={`resend-invite-${invite.id}`}
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => copyInviteLink(invite)}
                                title="Copy signup link"
                                data-testid={`copy-invite-link-${invite.id}`}
                              >
                                <Link2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => copyInviteCode(invite.code)}
                                title="Copy code only"
                                data-testid={`copy-invite-${invite.id}`}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => openEditInviteDialog(invite)}
                                title="Change access level"
                                data-testid={`edit-invite-${invite.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-red-500 hover:text-red-700"
                                onClick={() => deleteInvite(invite.id)}
                                data-testid={`delete-invite-${invite.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Member Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
              <DialogDescription>
                Update access level for {selectedMember?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Access Level</Label>
                <Select value={editAccessLevel} onValueChange={setEditAccessLevel}>
                  <SelectTrigger data-testid="edit-access-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACCESS_LEVELS).map(([key, { label, description }]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex flex-col">
                          <span>{label}</span>
                          <span className="text-xs text-gray-500">{description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={updateMember} data-testid="confirm-edit-member-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Invite Dialog */}
        <Dialog open={showEditInviteDialog} onOpenChange={setShowEditInviteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Invite Access Level</DialogTitle>
              <DialogDescription>
                Update access level for pending invite{selectedInvite?.invitee_name ? ` to ${selectedInvite.invitee_name}` : ''}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Access Level</Label>
                <Select value={editInviteAccessLevel} onValueChange={setEditInviteAccessLevel}>
                  <SelectTrigger data-testid="edit-invite-access-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACCESS_LEVELS).map(([key, { label, description }]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex flex-col">
                          <span>{label}</span>
                          <span className="text-xs text-gray-500">{description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditInviteDialog(false)}>Cancel</Button>
              <Button onClick={updateInvite} data-testid="confirm-edit-invite-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TeamPage;
